import { useCallback, useEffect, useState } from "react";
import { getRequest } from "helpers/utility/axiosServiceLayer";
import { composeApiURL } from "helpers/utility/axiosHelpers";
import { ScanResult, ScanResultsFilters, ScanResultsResponse } from "./types";

const HOST_URL = process.env.REACT_APP_API_ENDPOINT!;

/**
 * Fetches the DataServer scan-results index.
 *
 * Kept out of the CreateDataset redux slice on purpose: this list is read-only reference
 * data for one section, nothing else in the wizard reads it, and it should survive the
 * slice's resetStore() lifecycle without being entangled in it.
 */
/**
 * `enabled` holds the fetch back until the caller knows its filters - adding to a dataset
 * must not first load every scan result and then narrow to the dataset's Job and Setup.
 */
export default function useScanResults(
  filters: ScanResultsFilters,
  enabled = true,
) {
  const [scanResults, setScanResults] = useState<ScanResult[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { job, setup, lot, from, to } = filters;

  const fetchScanResults = useCallback(
    async (signal?: AbortSignal) => {
      setIsLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (job) params.set("job", job);
        if (setup) params.set("setup", setup);
        if (lot) params.set("lot", lot);
        if (from) params.set("from", from);
        if (to) params.set("to", to);

        const query = params.toString();
        const url = composeApiURL(
          HOST_URL,
          `/scan-results${query ? `?${query}` : ""}`,
        );
        const data = await getRequest<ScanResultsResponse>(url, { signal });

        setScanResults(data.scan_results);
        setTruncated(data.truncated);
      } catch (e: any) {
        if (e.name === "CanceledError" || e.code === "ERR_CANCELED") return;
        setError(
          e.response?.data?.detail ||
            "Failed to load scan results from DataServer",
        );
        setScanResults([]);
      } finally {
        setIsLoading(false);
      }
    },
    [job, setup, lot, from, to],
  );

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    fetchScanResults(controller.signal);
    return () => controller.abort();
  }, [fetchScanResults, enabled]);

  return {
    scanResults,
    truncated,
    isLoading,
    error,
    refetch: fetchScanResults,
  };
}
